import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaTimes, FaSearch, FaSpinner } from 'react-icons/fa';
import { rootUrl } from '../../../config/config';
import { formatMoney } from '../owners/formatMoney';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

function StatusBadge({ label, color }) {
    const colors = {
        green: 'bg-green-100 text-green-800',
        blue: 'bg-blue-100 text-blue-800',
        red: 'bg-red-100 text-red-800',
        yellow: 'bg-yellow-100 text-yellow-800',
    };
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.yellow}`}>
            {label}
        </span>
    );
}

function SuccessCard({ transId, inboundRow, imported }) {
    const badge = imported
        ? <StatusBadge label="Imported" color="green" />
        : <StatusBadge label="Already in system" color="blue" />;

    const formatDate = (ts) => {
        if (!ts) return '—';
        const d = new Date(ts);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
            + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    };

    const maskPhone = (phone) => {
        if (!phone) return '—';
        const s = String(phone);
        return s.length <= 4 ? s : '••••••' + s.slice(-4);
    };

    return (
        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-semibold text-gray-800">{inboundRow?.trans_id || transId}</span>
                {badge}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><span className="text-gray-500">Amount</span><div className="font-semibold text-gray-900">{inboundRow?.amount != null ? formatMoney(inboundRow.amount) : '—'}</div></div>
                <div><span className="text-gray-500">Date &amp; Time</span><div className="text-gray-900">{formatDate(inboundRow?.trans_time)}</div></div>
                <div><span className="text-gray-500">Payer</span><div className="text-gray-900">{[inboundRow?.first_name, inboundRow?.last_name].filter(Boolean).join(' ') || '—'}</div></div>
                <div><span className="text-gray-500">Phone</span><div className="text-gray-900">{maskPhone(inboundRow?.msisdn_hash)}</div></div>
                <div><span className="text-gray-500">Bill Ref</span><div className="text-gray-900">{inboundRow?.bill_ref_raw || '—'}</div></div>
                <div><span className="text-gray-500">Match Status</span><div className="text-gray-900 capitalize">{inboundRow?.match_status || '—'}</div></div>
                <div><span className="text-gray-500">Car Post</span><div className="text-gray-900 capitalize">{inboundRow?.post_status || '—'}</div></div>
                <div><span className="text-gray-500">Cashbook Post</span><div className="text-gray-900 capitalize">{inboundRow?.cb_posted || '—'}</div></div>
            </div>
        </div>
    );
}

function ErrorCard({ transId, status, message }) {
    const badgeMap = {
        failed: <StatusBadge label="Not found" color="red" />,
        error: <StatusBadge label="Error" color="red" />,
        timeout: <StatusBadge label="Timed out" color="yellow" />,
    };
    return (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-semibold text-gray-800">{transId}</span>
                {badgeMap[status] || <StatusBadge label="Error" color="red" />}
            </div>
            {message && <p className="text-sm text-red-700">{message}</p>}
        </div>
    );
}

const C2BLookupPanel = ({ onClose, onImported, authToken }) => {
    const [transId, setTransId] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const pollRef = useRef(null);
    const timeoutRef = useRef(null);

    const authHeaders = { Authorization: `Bearer ${authToken}` };

    const clearPolling = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        pollRef.current = null;
        timeoutRef.current = null;
    };

    useEffect(() => () => clearPolling(), []);

    const startPolling = (lookupId, submittedTransId) => {
        const poll = async () => {
            try {
                const res = await axios.get(`${rootUrl}/api/payments/c2b/lookup/${lookupId}`, { headers: authHeaders });
                const row = res.data?.data;
                if (!row) return;
                if (row.status === 'completed') {
                    clearPolling();
                    setResult({ type: 'success', imported: true, data: row.data, transId: submittedTransId });
                    setLoading(false);
                    onImported();
                } else if (row.status === 'failed') {
                    clearPolling();
                    setResult({ type: 'error', status: 'failed', message: row.result_desc || 'Transaction not found on Safaricom.', transId: submittedTransId });
                    setLoading(false);
                }
            } catch {
                // keep polling on transient network errors
            }
        };

        pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
        timeoutRef.current = setTimeout(() => {
            clearPolling();
            setResult({ type: 'error', status: 'timeout', message: 'Safaricom did not respond in time. Try again shortly.', transId: submittedTransId });
            setLoading(false);
        }, POLL_TIMEOUT_MS);

        poll();
    };

    const handleLookup = async () => {
        const code = transId.trim();
        if (!code) return;
        setLoading(true);
        setResult(null);
        clearPolling();

        try {
            const res = await axios.post(
                `${rootUrl}/api/payments/c2b/lookup`,
                { trans_id: code },
                { headers: authHeaders }
            );
            const body = res.data;

            if (body.status === 'already_exists') {
                setResult({ type: 'success', imported: false, data: body.data, transId: code });
                setLoading(false);
            } else if (body.status === 'pending') {
                startPolling(body.lookupId, code);
            } else {
                setResult({ type: 'error', status: 'error', message: body.message || 'Unexpected response.', transId: code });
                setLoading(false);
            }
        } catch (err) {
            setResult({ type: 'error', status: 'error', message: err.response?.data?.message || 'Request failed. Check your connection.', transId: code });
            setLoading(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <FaSearch className="text-[#FF5733]" /> Lookup Transaction
                </span>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                    <FaTimes />
                </button>
            </div>
            <div className="flex gap-2">
                <div className="flex-1">
                    <label htmlFor="c2b-lookup-code" className="block text-xs font-medium text-gray-600 mb-1">
                        M-PESA Transaction Code
                    </label>
                    <input
                        id="c2b-lookup-code"
                        type="text"
                        value={transId}
                        onChange={e => setTransId(e.target.value.toUpperCase())}
                        onKeyDown={e => e.key === 'Enter' && !loading && handleLookup()}
                        placeholder="e.g. OEI2AK4Q16"
                        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#FF5733]"
                        disabled={loading}
                    />
                </div>
                <div className="flex items-end">
                    <button
                        type="button"
                        onClick={handleLookup}
                        disabled={loading || !transId.trim()}
                        aria-label="Lookup"
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? <FaSpinner className="animate-spin" /> : <FaSearch />}
                        Lookup
                    </button>
                </div>
            </div>

            {loading && (
                <p className="mt-3 text-sm text-gray-500 flex items-center gap-2">
                    <FaSpinner className="animate-spin text-[#FF5733]" />
                    Checking with Safaricom…
                </p>
            )}

            {result?.type === 'success' && (
                <SuccessCard transId={result.transId} inboundRow={result.data} imported={result.imported} />
            )}
            {result?.type === 'error' && (
                <ErrorCard transId={result.transId} status={result.status} message={result.message} />
            )}
        </div>
    );
};

export default C2BLookupPanel;
